import { Box, Skeleton, styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import { forwardRef, ReactNode } from 'react';
import { useRecoilValue } from 'recoil';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { IdField } from '.';

/** Two-column grid of LabelText / ValueText pairs, aligned on the baseline. */
export const labelValueGridSx = {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '3px 6px',
    alignItems: 'baseline',
} as const;

export const LabelValueBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'inline',
})<{ inline?: boolean }>(({ theme, inline }) => ({
    display: inline ? 'flex' : 'block',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}));

export const LabelText = styled(Typography)(({ theme }) => ({
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.2,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    flexShrink: 0,
}));

export const ValueText = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'inline',
})<{ inline?: boolean; component?: React.ElementType }>(() => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.2,
    fontSize: 12,
    fontWeight: 500,
}));

/**
 * A value cell of a properties list: the value, then the pencil that edits
 * it when given. `tabIndex` -1 makes the cell a focus target for script
 * without putting it in the tab order.
 */
export const ValueCell = forwardRef<
    HTMLDivElement,
    { children: ReactNode; tabIndex?: number }
>(function ValueCell({ children, tabIndex }, ref) {
    return (
        <Box
            ref={ref}
            tabIndex={tabIndex}
            display="flex"
            alignItems="center"
            gap={0.5}
            minWidth={0}
        >
            {children}
        </Box>
    );
});

/**
 * A {@link ValueText} that wraps a long value onto further lines instead of
 * cutting it short. Past six lines it scrolls, so a long value cannot stretch
 * the panel around it.
 */
export const WrappingValueText = styled(ValueText)({
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    // Six lines, plus the padding below.
    maxHeight: 'calc(6lh + 1px)',
    overflowY: 'auto',
    // A line can overflow its box by a fraction of a pixel, which would make
    // every short value scroll; the padding takes that fraction.
    paddingBottom: '1px',
});

export const Textarea = styled(Typography)(() => ({
    lineHeight: 1.2,
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
}));

type LabelValueProps = {
    id: string;
    label: string;
    inline?: boolean;
};

export const LabelIdValue = ({
    id,
    label,
    inline = false,
}: LabelValueProps) => {
    return (
        <LabelValueBox inline={inline}>
            <LabelText>{`${label}`}</LabelText>
            <IdField
                id={id}
                label={undefined}
                typographyProps={{ fontSize: inline ? 12 : 14 }}
            />
        </LabelValueBox>
    );
};

type LabeledValueProps = {
    label: string | ReactNode;
    value?: string | number | ReactNode;
    loading?: boolean;
    inline?: boolean;
    sensitive?: boolean;
    testId?: string;
};

export const LabeledValue = ({
    label,
    value = 'Unknown',
    loading = false,
    inline = false,
    sensitive = false,
    testId = undefined,
}: LabeledValueProps) => {
    const randomWidth = 50 + Math.random() * 100;
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    // Suppress the native browser tooltip for sensitive values when hidden,
    // otherwise hovering would reveal the unblurred PHI string.
    const safeTitle =
        typeof value === 'string' && (!sensitive || showSensitiveInfo)
            ? value
            : undefined;
    return (
        <LabelValueBox inline={inline}>
            <LabelText title={safeTitle}>{label}</LabelText>
            {loading ? (
                <Skeleton width={randomWidth} height={14} />
            ) : (
                <ValueText
                    component="span"
                    inline={inline}
                    title={safeTitle}
                    sx={{
                        filter:
                            sensitive && !showSensitiveInfo
                                ? 'blur(0.5rem)'
                                : 'none',
                        userSelect:
                            sensitive && !showSensitiveInfo ? 'none' : 'auto',
                    }}
                    data-sensitive={sensitive && showSensitiveInfo}
                    data-testid={testId}
                >
                    {value ?? 'Unknown'}
                </ValueText>
            )}
        </LabelValueBox>
    );
};

export const LabeledTextarea = ({
    label,
    value = 'Unknown',
    loading = false,
}: LabeledValueProps) => {
    const randomWidth = 50 + Math.random() * 100;
    return (
        <LabelValueBox>
            <LabelText>{label}</LabelText>
            {loading ? (
                <Skeleton width={randomWidth} height={14} />
            ) : (
                <Textarea>{value ?? 'Unknown'}</Textarea>
            )}
        </LabelValueBox>
    );
};
